package com.schoolconnect.app.repository.school

import com.schoolconnect.app.datasource.network.KtorApiClient
import com.schoolconnect.app.datasource.network.request.SchoolCreateRequest
import com.schoolconnect.app.datasource.network.request.FileUploadRequest
import com.schoolconnect.app.datasource.network.request.SchoolUpdateRequest
import com.schoolconnect.app.datasource.network.response.SchoolResponse
import com.schoolconnect.app.datasource.network.response.serverTotalCountOrNull
import com.schoolconnect.app.datasource.network.response.withServerTotalCount
import com.schoolconnect.app.datasource.network.response.RecordListResponse
import com.schoolconnect.app.datasource.network.response.DashboardSchoolsAnalyticsResponse
import com.schoolconnect.app.datasource.network.UserSessionStore
import com.schoolconnect.app.repository.common.CachedValue
import com.schoolconnect.app.repository.common.loadPagedItems
import com.schoolconnect.app.feature.common.PaginationPolicy
import com.schoolconnect.app.feature.common.ServerSort
import com.schoolconnect.app.repository.common.loadRefreshableCachedValue
import com.schoolconnect.app.repository.common.returnCachedAndRefresh
import com.schoolconnect.app.util.scLog
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.SupervisorJob

object SchoolRepository {
    private const val TAG = "[DATA_RETRIEVE_SC] SchoolRepository"
    private const val PAGE_SIZE = 100
    private const val MAX_PAGES = 100

    private val scope = CoroutineScope(SupervisorJob() + Dispatchers.Default)

    private data class SchoolsCacheKey(
        val query: String,
        val isActive: Boolean?
    )

    private data class CachedSchools(
        val key: SchoolsCacheKey,
        val schools: List<SchoolResponse>
    )

    private data class SchoolsPageCacheKey(
        val query: String,
        val isActive: Boolean?,
        val withStudentCounts: Boolean,
        val pageIndex: Int,
        val pageSize: Int,
        val sort: String
    )

    private data class CachedSchoolsPage(
        val key: SchoolsPageCacheKey,
        val schools: List<SchoolResponse>
    )

    private data class SchoolByCodeCacheEntry(
        val code: String,
        val school: SchoolResponse
    )

    private var cachedSchools: CachedSchools? = null
    private var cachedSchoolsPage: CachedSchoolsPage? = null
    private var cachedSchoolByCode: SchoolByCodeCacheEntry? = null
    private var cachedSchoolsAnalytics: CachedValue<DashboardSchoolsAnalyticsResponse>? = null

    suspend fun loadSchools(
        query: String = "",
        isActive: Boolean? = null,
        forceRemote: Boolean = false
    ): Result<List<SchoolResponse>> {
        val normalizedQuery = query.trim()
        val cacheKey = SchoolsCacheKey(query = normalizedQuery, isActive = isActive)
        cachedSchools?.takeIf { !forceRemote && it.key == cacheKey }?.let { cache ->
            scLog(
                "$TAG loadSchools cacheHit q=${normalizedQuery.ifBlank { "<blank>" }} isActive=${isActive?.toString() ?: "<any>"} count=${cache.schools.size}"
            )
            return returnCachedAndRefresh(
                scope = scope,
                tag = TAG,
                operation = "loadSchools",
                context = "q=${normalizedQuery.ifBlank { "<blank>" }} isActive=${isActive?.toString() ?: "<any>"}",
                cached = cache.schools,
                refresh = { loadSchools(query, isActive, forceRemote = true) }
            )
        }
        scLog(
            "$TAG loadSchools start q=${normalizedQuery.ifBlank { "<blank>" }} isActive=${isActive?.toString() ?: "<any>"} forceRemote=$forceRemote"
        )
        return runCatching {
            val schools = loadPagedItems(
                tag = TAG,
                operation = "loadSchools",
                pageSize = PAGE_SIZE,
                maxPages = MAX_PAGES,
                startContext = "q=${normalizedQuery.ifBlank { "<blank>" }} isActive=${isActive?.toString() ?: "<any>"}"
            ) { offset ->
                val page = KtorApiClient.getSchools(
                    q = normalizedQuery.ifBlank { null },
                    isActive = isActive,
                    limit = PAGE_SIZE,
                    offset = offset
                ).getOrElse { throw it }

                scLog("$TAG loadSchools page offset=$offset count=${page.size}")
                page
            }
            cachedSchools = CachedSchools(cacheKey, schools.toList())
            schools
        }
    }

    suspend fun loadSchoolsPage(
        query: String = "",
        isActive: Boolean? = null,
        pageIndex: Int = 0,
        pageSize: Int = PaginationPolicy.DEFAULT_PAGE_SIZE,
        sort: ServerSort = ServerSort("name", com.schoolconnect.app.feature.common.ServerSortOrder.ASC),
        withStudentCounts: Boolean = false,
        forceRemote: Boolean = false
    ): Result<List<SchoolResponse>> {
        val normalizedQuery = query.trim()
        val normalizedPageIndex = pageIndex.coerceAtLeast(0)
        val normalizedPageSize = pageSize.coerceAtLeast(1)
        val cacheKey = SchoolsPageCacheKey(
            query = normalizedQuery,
            isActive = isActive,
            withStudentCounts = withStudentCounts,
            pageIndex = normalizedPageIndex,
            pageSize = normalizedPageSize,
            sort = sort.cacheKey
        )
        cachedSchoolsPage?.takeIf { !forceRemote && it.key == cacheKey }?.let { cache ->
            scLog(
                "$TAG loadSchoolsPage cacheHit q=${normalizedQuery.ifBlank { "<blank>" }} isActive=${isActive?.toString() ?: "<any>"} pageIndex=$normalizedPageIndex pageSize=$normalizedPageSize count=${cache.schools.size}"
            )
            return returnCachedAndRefresh(
                scope = scope,
                tag = TAG,
                operation = "loadSchoolsPage",
                context = "pageIndex=$normalizedPageIndex pageSize=$normalizedPageSize",
                cached = cache.schools,
                refresh = { loadSchoolsPage(query, isActive, normalizedPageIndex, normalizedPageSize, sort, withStudentCounts, forceRemote = true) }
            )
        }

        val offset = normalizedPageIndex * normalizedPageSize
        scLog(
            "$TAG loadSchoolsPage start q=${normalizedQuery.ifBlank { "<blank>" }} isActive=${isActive?.toString() ?: "<any>"} pageIndex=$normalizedPageIndex pageSize=$normalizedPageSize offset=$offset forceRemote=$forceRemote"
        )
        return runCatching {
            val response = if (withStudentCounts) {
                KtorApiClient.getSchoolsWithStudentCounts(
                    q = normalizedQuery.ifBlank { null },
                    isActive = isActive,
                    limit = normalizedPageSize,
                    offset = offset,
                    sortBy = sort.normalizedField,
                    order = sort.order.apiValue
                )
            } else {
                KtorApiClient.getSchools(
                    q = normalizedQuery.ifBlank { null },
                    isActive = isActive,
                    limit = normalizedPageSize,
                    offset = offset,
                    sortBy = sort.normalizedField,
                    order = sort.order.apiValue
                )
            }.getOrElse { throw it }
            val page = response.withServerTotalCount(response.serverTotalCountOrNull())
            scLog("$TAG loadSchoolsPage complete q=${normalizedQuery.ifBlank { "<blank>" }} pageIndex=$normalizedPageIndex pageSize=$normalizedPageSize count=${page.size} total=${page.serverTotalCountOrNull() ?: "<unknown>"}")
            cachedSchoolsPage = CachedSchoolsPage(
                cacheKey,
                page.toList().withServerTotalCount(page.serverTotalCountOrNull())
            )
            page
        }
    }

    suspend fun loadSchoolByCode(code: String, forceRemote: Boolean = false): Result<SchoolResponse> {
        val normalizedCode = code.trim()
        cachedSchoolByCode?.takeIf { !forceRemote && it.code == normalizedCode }?.let { cache ->
            scLog("$TAG loadSchoolByCode cacheHit code=${normalizedCode.ifBlank { "<blank>" }} id=${cache.school.id.orEmpty()}")
            return returnCachedAndRefresh(
                scope = scope,
                tag = TAG,
                operation = "loadSchoolByCode",
                context = "code=$normalizedCode",
                cached = cache.school,
                refresh = { loadSchoolByCode(code, forceRemote = true) }
            )
        }
        scLog("$TAG loadSchoolByCode start code=${normalizedCode.ifBlank { "<blank>" }}")
        return runCatching {
            val school = KtorApiClient.getSchoolByCode(normalizedCode).getOrElse { throw it }
            scLog("$TAG loadSchoolByCode complete code=${school.code.orEmpty().ifBlank { normalizedCode }} id=${school.id.orEmpty()}")
            cachedSchoolByCode = SchoolByCodeCacheEntry(normalizedCode, school)
            school
        }
    }

    suspend fun resolveSchoolCodeById(schoolId: String, forceRemote: Boolean = false): Result<String> {
        val normalizedId = schoolId.trim()
        if (normalizedId.isBlank()) {
            return Result.failure(IllegalArgumentException("School id is blank"))
        }
        scLog("$TAG resolveSchoolCodeById start schoolId=$normalizedId forceRemote=$forceRemote")
        return runCatching {
            val schools = loadSchools(forceRemote = forceRemote).getOrElse { throw it }
            val resolved = schools.firstOrNull {
                it.id == normalizedId || it.code == normalizedId
            }?.code.orEmpty()
            if (resolved.isBlank()) {
                throw IllegalStateException("Unable to resolve school code for schoolId=$normalizedId")
            }
            scLog("$TAG resolveSchoolCodeById complete schoolId=$normalizedId schoolCode=$resolved")
            resolved
        }
    }

    suspend fun resolveSchoolNameById(schoolId: String, forceRemote: Boolean = false): Result<String> {
        val normalizedId = schoolId.trim()
        if (normalizedId.isBlank()) {
            return Result.failure(IllegalArgumentException("School id is blank"))
        }
        scLog("$TAG resolveSchoolNameById start schoolId=$normalizedId forceRemote=$forceRemote")
        return runCatching {
            val schools = loadSchools(forceRemote = forceRemote).getOrElse { throw it }
            val resolved = schools.firstOrNull {
                it.id == normalizedId || it.code == normalizedId
            }?.name.orEmpty()
            if (resolved.isBlank()) {
                throw IllegalStateException("Unable to resolve school name for schoolId=$normalizedId")
            }
            scLog("$TAG resolveSchoolNameById complete schoolId=$normalizedId schoolName=$resolved")
            resolved
        }
    }

    fun peekSchoolsAnalytics(): DashboardSchoolsAnalyticsResponse? = cachedSchoolsAnalytics?.value

    suspend fun loadSchoolsAnalytics(forceRemote: Boolean = false): Result<DashboardSchoolsAnalyticsResponse> {
        return loadRefreshableCachedValue(
            scope = scope,
            tag = TAG,
            operation = "loadSchoolsAnalytics",
            context = "role=super_admin forceRemote=$forceRemote",
            cachedValue = if (forceRemote) null else cachedSchoolsAnalytics,
            fetchValue = { KtorApiClient.getSchoolsAnalytics() },
            updateCache = { response, fetchedAt ->
                cachedSchoolsAnalytics = CachedValue(response, fetchedAt)
            },
            successDetails = { response ->
                "scope=${response.scope.ifBlank { "<blank>" }} total=${response.totalSchools}"
            },
            forceRemote = forceRemote
        )
    }

    suspend fun createSchool(school: SchoolCreateRequest): Result<SchoolResponse> {
        scLog("$TAG createSchool start name=${school.name.trim().ifBlank { "<blank>" }}")
        return runCatching {
            val createdSchool = KtorApiClient.createSchool(school).getOrElse { throw it }
            scLog("$TAG createSchool complete code=${createdSchool.code.orEmpty().ifBlank { "<blank>" }} id=${createdSchool.id.orEmpty()}")
            invalidateCaches()
            createdSchool
        }
    }

    data class SchoolCreateOutcome(
        val school: SchoolResponse,
        val logoUploadFailure: Throwable? = null
    )

    suspend fun createSchoolWithLogo(
        school: SchoolCreateRequest,
        logoFileName: String,
        logoBytes: ByteArray?
    ): Result<SchoolCreateOutcome> {
        return runCatching {
            val normalizedBytes = logoBytes?.takeIf { it.isNotEmpty() }
            val schoolWithLogoId = normalizedBytes?.let { bytes ->
                val upload = uploadSchoolLogoFile(
                    schoolId = null,
                    fileName = logoFileName,
                    bytes = bytes
                ).getOrElse { throw it }
                val recordId = upload.recordId.trim()
                if (recordId.isBlank()) {
                    error("School logo upload response did not contain record_id")
                }
                school.copy(id = recordId)
            } ?: school.copy(id = null)
            val createdSchool = createSchool(schoolWithLogoId).getOrElse { throw it }
            SchoolCreateOutcome(createdSchool)
        }
    }

    data class SchoolLogoUploadResult(
        val fileId: String,
        val recordId: String = ""
    )

    suspend fun uploadSchoolLogoFile(
        schoolId: String?,
        fileName: String,
        bytes: ByteArray
    ): Result<SchoolLogoUploadResult> {
        val normalizedId = schoolId?.trim().orEmpty().ifBlank { null }
        val normalizedFileName = fileName.trim().ifBlank { "school-logo" }
        if (bytes.isEmpty()) return Result.failure(IllegalArgumentException("School logo must not be empty"))

        val contentType = when (normalizedFileName.substringAfterLast('.', "").lowercase()) {
            "png" -> "image/png"
            "jpg", "jpeg" -> "image/jpeg"
            "webp" -> "image/webp"
            else -> "application/octet-stream"
        }
        scLog("$TAG uploadSchoolLogoFile start schoolId=${normalizedId ?: "<new>"} fileName=$normalizedFileName bytes=${bytes.size}")

        return runCatching {
            val grant = KtorApiClient.requestFileUpload(
                FileUploadRequest(
                    module = "SCHOOL_LOGO",
                    recordId = normalizedId,
                    filename = normalizedFileName,
                    contentType = contentType,
                    sizeBytes = bytes.size.toLong()
                )
            ).getOrElse { throw it }
            val fileId = grant.fileId?.trim().orEmpty()
            if (fileId.isBlank()) error("School logo upload response did not contain file_id")
            KtorApiClient.uploadFileBytes(grant, bytes, contentType).getOrElse { throw it }
            KtorApiClient.confirmFileUpload(fileId).getOrElse { throw it }
            scLog("$TAG uploadSchoolLogoFile complete schoolId=${normalizedId ?: "<new>"}")
            SchoolLogoUploadResult(
                fileId = fileId,
                recordId = (grant.recordId ?: grant.reservedEntityId).orEmpty()
            )
        }
    }

    suspend fun updateSchool(code: String, school: SchoolUpdateRequest): Result<SchoolResponse> {
        val normalizedCode = code.trim()
        val currentUserId = UserSessionStore.getUserId().trim()
        scLog("$TAG updateSchool start code=${normalizedCode.ifBlank { "<blank>" }}")
        return runCatching {
            val updatedSchool = KtorApiClient.updateSchool(normalizedCode, school, currentUserId).getOrElse { throw it }
            scLog("$TAG updateSchool complete code=${normalizedCode.ifBlank { "<blank>" }} id=${updatedSchool.id}")
            invalidateCaches()
            updatedSchool
        }
    }

    suspend fun toggleSchoolStatus(
        code: String,
        isActive: Boolean
    ): Result<Unit> {
        val normalizedCode = code.trim()
        scLog(
            "$TAG toggleSchoolStatus start code=${normalizedCode.ifBlank { "<blank>" }} isActive=$isActive"
        )
        return runCatching {
            KtorApiClient.toggleSchoolStatus(normalizedCode, isActive).getOrElse { throw it }
            scLog(
                "$TAG toggleSchoolStatus complete code=${normalizedCode.ifBlank { "<blank>" }} isActive=$isActive"
            )
            invalidateCaches()
        }
    }

    suspend fun deleteSchool(code: String): Result<Unit> {
        val normalizedCode = code.trim()
        if (normalizedCode.isBlank()) {
            return Result.failure(IllegalArgumentException("School code is blank"))
        }
        scLog("$TAG deleteSchool start code=$normalizedCode")
        return runCatching {
            KtorApiClient.deleteSchool(normalizedCode).getOrElse { throw it }
            scLog("$TAG deleteSchool complete code=$normalizedCode")
            invalidateCaches()
        }.onFailure { throwable ->
            scLog("$TAG deleteSchool failure code=$normalizedCode", throwable)
        }
    }

    fun invalidateCaches() {
        cachedSchools = null
        cachedSchoolsPage = null
        cachedSchoolByCode = null
        cachedSchoolsAnalytics = null
    }
}
